import { getStoredToken } from './authService';
import { API_BASE_URL } from '../config/api';

// ========================================
// Types for Feeds Service
// ========================================

export interface RSSSource {
  source_id: string;
  user_id: string;
  source_name: string;
  source_url: string;
  site_url: string | null;
  description: string | null;
  last_fetched_at: string | null;
  fetch_error: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface RSSSourceWithCounts extends RSSSource {
  item_count: number;
  unread_count: number;
  starred_count?: number;
}

export interface RSSItem {
  item_id: string;
  source_id: string;
  user_id: string;
  title: string;
  link: string;
  description: string | null;
  content: string | null;
  author: string | null;
  published_date: string | null;
  guid: string | null;
  categories: string[] | null;
  enclosures: Array<{
    url: string;
    type: string;
    length: number;
  }> | null;
  is_read: boolean;
  is_starred: boolean;
  created_at: string;
  source_name?: string;
  source_site_url?: string;
}

export interface DiscoveredFeed {
  url: string;
  title: string;
  type: string;
  foundBy: string;
  itemCount: number;
}

export interface GetItemsFilters {
  sourceId?: string;
  limit?: number;
  offset?: number;
  isRead?: boolean;
  isStarred?: boolean;
  sortBy?: 'published_date' | 'created_at' | 'title';
  order?: 'asc' | 'desc';
}

export interface MarkReadOptions {
  itemIds?: string[];
  sourceId?: string;
  beforeDate?: string;
}

// Helper function to get auth token
const getAuthToken = async (): Promise<string> => {
  const token = getStoredToken();
  if (!token) {
    throw new Error('User not authenticated');
  }
  return token;
};

// Helper function to make authenticated API calls
const apiCall = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API call failed for ${endpoint}:`, error);
    throw error;
  }
};

// ========================================
// Feeds Service Class
// ========================================

export class FeedsService {
  /**
   * Get all RSS sources for the user
   */
  public static async getSources(
    active?: boolean,
    sortBy: string = 'created_at',
    order: string = 'desc'
  ): Promise<RSSSourceWithCounts[]> {
    try {
      const params = new URLSearchParams();
      if (active !== undefined) params.append('active', String(active));
      params.append('sortBy', sortBy);
      params.append('order', order);

      const result = await apiCall(`/feeds/sources?${params.toString()}`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch RSS sources');
      }

      return result.data.sources;
    } catch (error) {
      console.error('📡 [FeedsService] Error fetching sources:', error);
      throw error;
    }
  }

  /**
   * Get specific RSS source details
   */
  public static async getSource(sourceId: string): Promise<RSSSourceWithCounts> {
    try {
      const result = await apiCall(`/feeds/sources/${sourceId}`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch RSS source');
      }

      return result.data;
    } catch (error) {
      console.error('📡 [FeedsService] Error fetching source:', error);
      throw error;
    }
  }

  /**
   * Create a new RSS source
   */
  public static async createSource(
    sourceUrl: string,
    sourceName?: string
  ): Promise<{ source: RSSSource; itemsInserted: number }> {
    try {
      const result = await apiCall('/feeds/sources', {
        method: 'POST',
        body: JSON.stringify({
          sourceUrl,
          sourceName: sourceName || undefined
        })
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to create RSS source');
      }

      return result.data;
    } catch (error) {
      console.error('📡 [FeedsService] Error creating source:', error);
      throw error;
    }
  }

  /**
   * Update RSS source
   */
  public static async updateSource(
    sourceId: string,
    updates: { sourceName?: string; isActive?: boolean }
  ): Promise<RSSSource> {
    try {
      const result = await apiCall(`/feeds/sources/${sourceId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to update RSS source');
      }

      return result.data;
    } catch (error) {
      console.error('📡 [FeedsService] Error updating source:', error);
      throw error;
    }
  }

  /**
   * Delete RSS source
   */
  public static async deleteSource(sourceId: string): Promise<void> {
    try {
      const result = await apiCall(`/feeds/sources/${sourceId}`, {
        method: 'DELETE'
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete RSS source');
      }
    } catch (error) {
      console.error('📡 [FeedsService] Error deleting source:', error);
      throw error;
    }
  }

  /**
   * Refresh RSS source - fetch latest items
   */
  public static async refreshSource(
    sourceId: string
  ): Promise<{ totalItems: number; newItems: number; skippedDuplicates: number }> {
    try {
      const result = await apiCall(`/feeds/sources/${sourceId}/refresh`, {
        method: 'POST'
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to refresh RSS source');
      }

      return result.data;
    } catch (error) {
      console.error('📡 [FeedsService] Error refreshing source:', error);
      throw error;
    }
  }

  /**
   * Discover RSS feeds from a website URL
   */
  public static async discoverFeeds(url: string): Promise<DiscoveredFeed[]> {
    try {
      const result = await apiCall('/feeds/discover', {
        method: 'POST',
        body: JSON.stringify({ url })
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to discover RSS feeds');
      }

      return result.data.feeds;
    } catch (error) {
      console.error('📡 [FeedsService] Error discovering feeds:', error);
      throw error;
    }
  }

  /**
   * Get RSS items with filters
   */
  public static async getItems(
    filters: GetItemsFilters = {}
  ): Promise<{ items: RSSItem[]; total: number; limit: number; offset: number }> {
    try {
      const params = new URLSearchParams();
      
      if (filters.sourceId) params.append('sourceId', filters.sourceId);
      if (filters.limit !== undefined) params.append('limit', String(filters.limit));
      if (filters.offset !== undefined) params.append('offset', String(filters.offset));
      if (filters.isRead !== undefined) params.append('isRead', String(filters.isRead));
      if (filters.isStarred !== undefined) params.append('isStarred', String(filters.isStarred));
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.order) params.append('order', filters.order);

      const result = await apiCall(`/feeds/items?${params.toString()}`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch RSS items');
      }

      return result.data;
    } catch (error) {
      console.error('📡 [FeedsService] Error fetching items:', error);
      throw error;
    }
  }

  /**
   * Get specific RSS item
   */
  public static async getItem(itemId: string): Promise<RSSItem> {
    try {
      const result = await apiCall(`/feeds/items/${itemId}`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch RSS item');
      }

      return result.data;
    } catch (error) {
      console.error('📡 [FeedsService] Error fetching item:', error);
      throw error;
    }
  }

  /**
   * Update RSS item (mark as read/starred)
   */
  public static async updateItem(
    itemId: string,
    updates: { isRead?: boolean; isStarred?: boolean }
  ): Promise<RSSItem> {
    try {
      const result = await apiCall(`/feeds/items/${itemId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to update RSS item');
      }

      return result.data;
    } catch (error) {
      console.error('📡 [FeedsService] Error updating item:', error);
      throw error;
    }
  }

  /**
   * Delete RSS item
   */
  public static async deleteItem(itemId: string): Promise<void> {
    try {
      const result = await apiCall(`/feeds/items/${itemId}`, {
        method: 'DELETE'
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete RSS item');
      }
    } catch (error) {
      console.error('📡 [FeedsService] Error deleting item:', error);
      throw error;
    }
  }

  /**
   * Bulk mark items as read
   */
  public static async markItemsAsRead(options: MarkReadOptions): Promise<{ updatedCount: number }> {
    try {
      const result = await apiCall('/feeds/items/mark-read', {
        method: 'POST',
        body: JSON.stringify(options)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to mark items as read');
      }

      return result.data;
    } catch (error) {
      console.error('📡 [FeedsService] Error marking items as read:', error);
      throw error;
    }
  }
}

